import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ConversationViewModel } from '../types/conversation/ConversationViewModel';
import { apiClient } from '../client/client';

/**
 * Query params for GET /api/conversation (paginated list).
 */
export interface ConversationListParams {
  ListingId?: string;
  SenderId?: string;
  ReceiverId?: string;
  'Pagination.PageSize'?: number;
  'Pagination.PageNumber'?: number;
}

export interface MyConversationsParams {
  pageNumber?: number;
  pageSize?: number;
  withUnread?: boolean;
}

/**
 * Conversation-with-messages response shape; mirrors backend
 * ConversationWithMessagesViewModel, including the new listing summary.
 */
export type ConversationWithMessages = ConversationViewModel & {
  pageNumber?: number;
  pageSize?: number;
  totalPageCount?: number;
};

/**
 * Conversation service – contact seller, list conversations, close conversation.
 */
export class ConversationService {
  private static readonly BASE = '/api/Conversation';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * POST /api/Conversation/contact-seller
   * Creates or returns existing conversation for this listing; returns ConversationViewModel
   * pre-populated with listingSummary for the chat-context banner.
   */
  async contactSeller(listingId: string): Promise<ServiceResultT<ConversationViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ConversationViewModel>>(
      `${ConversationService.BASE}/contact-seller`,
      { listingId }
    );
    return data;
  }

  async closeConversation(conversationId: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${ConversationService.BASE}/${conversationId}/close`
    );
    return data;
  }

  async markMessagesRead(conversationId: string): Promise<ServiceResultT<number>> {
    const { data } = await this.client.put<ServiceResultT<number>>(
      `${ConversationService.BASE}/${conversationId}/messages/read`
    );
    return data;
  }

  /**
   * GET /api/Conversation/unread-message-count
   * @param options.excludeConversationId while this thread is open, omit its unread (incoming) from the aggregate
   */
  async getUnreadMessageCount(options?: { excludeConversationId?: string }): Promise<ServiceResultT<number>> {
    const params: Record<string, string> = {};
    if (options?.excludeConversationId) {
      params.excludeConversationId = options.excludeConversationId;
    }
    const { data } = await this.client.get<ServiceResultT<number>>(
      `${ConversationService.BASE}/unread-message-count`,
      { params: Object.keys(params).length > 0 ? params : undefined }
    );
    return data;
  }

  /**
   * GET /api/Conversation – paginated list with optional filters (legacy admin-style call).
   */
  async getConversations(
    params?: ConversationListParams
  ): Promise<ServiceResultT<ConversationViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ConversationViewModel[]>>(
      ConversationService.BASE,
      { params: params ?? {} }
    );
    return data;
  }

  /**
   * GET /api/Conversation/my – current user's conversations, enriched with listing context,
   * last-message preview and (optionally) unread counts.
   */
  async getMyConversations(
    options?: MyConversationsParams
  ): Promise<ServiceResultT<ConversationViewModel[]>> {
    const params: Record<string, unknown> = {};
    if (options?.pageNumber != null) params.pageNumber = options.pageNumber;
    if (options?.pageSize != null) params.pageSize = options.pageSize;
    if (options?.withUnread != null) params.withUnread = options.withUnread;
    const { data } = await this.client.get<ServiceResultT<ConversationViewModel[]>>(
      `${ConversationService.BASE}/my`,
      { params }
    );
    return data;
  }

  async getById(conversationId: string): Promise<ServiceResultT<ConversationViewModel>> {
    const { data } = await this.client.get<ServiceResultT<ConversationViewModel>>(
      `${ConversationService.BASE}/${conversationId}`
    );
    return data;
  }

  /**
   * GET /api/Conversation/{id}/messages – paginated messages plus listing-summary context.
   */
  async getConversationWithMessages(
    conversationId: string,
    pageNumber?: number,
    pageSize?: number
  ): Promise<ServiceResultT<ConversationWithMessages>> {
    const params: Record<string, unknown> = {};
    if (pageNumber != null) params.pageNumber = pageNumber;
    if (pageSize != null) params.pageSize = pageSize;
    const { data } = await this.client.get<ServiceResultT<ConversationWithMessages>>(
      `${ConversationService.BASE}/${conversationId}/messages`,
      { params }
    );
    return data;
  }
}

export const conversationService = new ConversationService();
